import React from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import useMeta from "../../base/hooks/useMeta";
import useForm from "../../base/hooks/form/useForm";
import useFormActions from "../../base/hooks/form/useFormActions";
import {
    MUTATION_UPDATE_JANE_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import { MUTATION_SYNC_FROM_JANE } from "../../apollo/module-operations/jane";
import _ from "lodash";
import useValidator from "../../base/hooks/form/useValidator";
import { useMutation } from "@apollo/react-hooks";
import useNotification from "../../base/hooks/components/useNotification";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const SyncIcon = () => <Icon icon="refresh" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const JaneForm = ({ app }) => {
    const notify = useNotification();

    const { user } = useMeta();
    const { inputs, setInputs, inputChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    let options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                id: inputs.id,
                app_id: inputs.app_id,
                api_key: inputs.api_key,
                store_id: inputs.store_id,
                product_index: inputs.product_index,
                stores_index: inputs.stores_index,

                reviews_index: inputs.reviews_index,
                client_id: inputs.client_id,
                client_secret: inputs.client_secret,
                host: inputs.host,

                type: "Jane",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "id",
                method: "isEmpty",
                validWhen: false,
                message: "This app id is required.",
            },

            {
                field: "app_id",
                method: "isEmpty",
                validWhen: false,
                message: "This app id is required.",
            },

            {
                field: "api_key",
                method: "isEmpty",
                validWhen: false,
                message: "This api key is required.",
            },

            {
                field: "store_id",
                method: "isEmpty",
                validWhen: false,
                message: "This store id is required.",
            },

            {
                field: "product_index",
                method: "isEmpty",
                validWhen: false,
                message: "This product index is required.",
            },

            {
                field: "reviews_index",
                method: "isEmpty",
                validWhen: false,
                message: "This review index is required.",
            },

            {
                field: "stores_index",
                method: "isEmpty",
                validWhen: false,
                message: "This store index is required.",
            },

            {
                field: "client_id",
                method: "isEmpty",
                validWhen: false,
                message: "This client id is required.",
            },

            {
                field: "client_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This client secret is required.",
            },

            {
                field: "operation_client_id",
                method: "isEmpty",
                validWhen: false,
                message: "This client id is required.",
            },

            {
                field: "operation_client_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This client secret is required.",
            },

            {
                field: "host",
                method: "isEmpty",
                validWhen: false,
                message: "This host is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Jane",
        "",
        MUTATION_UPDATE_JANE_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const [mutateSyncFromJane, { loading: syncLoading }] = useMutation(
        MUTATION_SYNC_FROM_JANE,
        {
            onCompleted: () => {
                notify("Sync Product From Jane Successfully", "success");
            },
            onError: () => {
                notify("Error Trying to Sync Product", "error");
            },
        },
    );

    const syncProducstFromJane = async () => {
        mutateSyncFromJane({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
            },
        });
    };

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Jane</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Jane Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "sync-products",
                                label: "Sync Products",
                                icon: syncLoading ? undefined : <SyncIcon />,
                                loading: syncLoading,
                                disabled: syncLoading,
                                variant: "secondary",
                                onClick: syncProducstFromJane,
                            },
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Jane actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Algolia">
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, "app_id")}
                                fullWidth
                                label="App Id"
                                name="app_id"
                                onChange={inputChange}
                                value={inputs.app_id || ""}
                            />
                            <Input
                                error={fieldError(validator, "api_key")}
                                fullWidth
                                label="Api Key"
                                name="api_key"
                                onChange={inputChange}
                                value={inputs.api_key || ""}
                            />
                            <Input
                                error={fieldError(validator, "product_index")}
                                fullWidth
                                label="Product Index"
                                name="product_index"
                                onChange={inputChange}
                                value={inputs.product_index || ""}
                            />
                            <div />
                            <Input
                                error={fieldError(validator, "reviews_index")}
                                fullWidth
                                label="Review Index"
                                name="reviews_index"
                                onChange={inputChange}
                                value={inputs.reviews_index || ""}
                            />
                            <div />
                            <Input
                                error={fieldError(validator, "stores_index")}
                                fullWidth
                                label="Stores Index"
                                name="stores_index"
                                onChange={inputChange}
                                value={inputs.stores_index || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="API Key">
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, "host")}
                                fullWidth
                                label="Host"
                                name="host"
                                onChange={inputChange}
                                value={inputs.host || ""}
                            />
                            <div />
                            <Input
                                error={fieldError(validator, "store_id")}
                                fullWidth
                                label="Store Id"
                                name="store_id"
                                onChange={inputChange}
                                value={inputs.store_id || ""}
                            />
                            <div />
                            <Input
                                error={fieldError(validator, "client_id")}
                                fullWidth
                                label="Client Id"
                                name="client_id"
                                onChange={inputChange}
                                value={inputs.client_id || ""}
                            />
                            <Input
                                error={fieldError(validator, "client_secret")}
                                fullWidth
                                label="Client Secret"
                                name="client_secret"
                                onChange={inputChange}
                                value={inputs.client_secret || ""}
                            />
                            <Input
                                error={fieldError(
                                    validator,
                                    "operation_client_id",
                                )}
                                fullWidth
                                label="Operation Client Id"
                                name="operation_client_id"
                                onChange={inputChange}
                                value={inputs.operation_client_id || ""}
                            />
                            <Input
                                error={fieldError(
                                    validator,
                                    "operation_client_secret",
                                )}
                                fullWidth
                                label="Operation Client Secret"
                                name="operation_client_secret"
                                onChange={inputChange}
                                value={inputs.operation_client_secret || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="User Auth">
                        <Switch
                            checked={Boolean(inputs.auto_migrate_user)}
                            label="Auto Migrate User"
                            name="auto_migrate_user"
                            onChange={inputChange}
                        />
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default JaneForm;
